import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Floating dropdown rendered via React portal into document.body.
 * Positions itself relative to the `anchorRef`'s bounding box, so it is
 * never clipped by overflow (overflow-x-auto tables), sticky toolbars, or
 * stacking contexts. Auto-flips upward when space below is tight.
 *
 * Props:
 *   anchorRef   – ref to the element to anchor to (input or wrapper)
 *   open        – when true, the dropdown is rendered
 *   maxHeight   – cap on dropdown height in px (default 256)
 *   zIndex      – stacking order (default 9999)
 *   className   – class list for the dropdown container
 *   children    – contents of the dropdown
 */
export default function PortalDropdown({
  anchorRef,
  open,
  maxHeight = 256,
  zIndex = 9999,
  className = "",
  children,
}) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setRect(null);
      return;
    }
    const el = anchorRef.current;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, bottom: r.bottom, width: r.width });
    };
    measure();
    const onEvt = () => measure();
    window.addEventListener("scroll", onEvt, true);
    window.addEventListener("resize", onEvt);
    return () => {
      window.removeEventListener("scroll", onEvt, true);
      window.removeEventListener("resize", onEvt);
    };
  }, [open, anchorRef]);

  if (!open || !rect) return null;

  const belowSpace = window.innerHeight - rect.bottom;
  const aboveSpace = rect.top;
  const flipUp = belowSpace < Math.min(maxHeight, 200) && aboveSpace > belowSpace;

  const style = {
    position: "fixed",
    left: rect.left,
    width: rect.width,
    zIndex,
    maxHeight,
  };
  if (flipUp) {
    style.bottom = window.innerHeight - rect.top + 4;
  } else {
    style.top = rect.bottom + 4;
  }

  return createPortal(
    <div className={className} style={style}>{children}</div>,
    document.body
  );
}
